import { useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { BROKER_TYPES } from '../utils/constants';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Search, Tags } from 'lucide-react';

export default function BrokerTagging() {
  const { brokerData, brokerTags, setBrokerTags } = useApp();
  const [search, setSearch] = useState('');

  const allBrokers = useMemo(() => {
    const set = new Set(brokerData.map(r => r.broker).filter(Boolean));
    return [...set].sort();
  }, [brokerData]);

  const filtered = useMemo(() => {
    if (!search) return allBrokers;
    const q = search.toLowerCase();
    return allBrokers.filter(b => b.toLowerCase().includes(q));
  }, [allBrokers, search]);

  const presetBrokers = useMemo(() => {
    return Object.entries(brokerTags)
      .filter(([name]) => !allBrokers.includes(name))
      .sort(([a], [b]) => a.localeCompare(b));
  }, [brokerTags, allBrokers]);

  const handleTagChange = (broker, tag) => {
    setBrokerTags(prev => ({ ...prev, [broker]: tag }));
  };

  const getTagColor = (tag) => {
    const colors = {
      BANK: 'text-trading-blue',
      INSTITUTIONAL: 'text-trading-green',
      FOREIGN: 'text-trading-amber',
      RETAIL: 'text-trading-muted',
      TRADER: 'text-purple-400',
      OTHER: 'text-trading-muted/60',
    };
    return colors[tag] || 'text-trading-muted';
  };

  return (
    <div className="p-6 max-w-[1200px] mx-auto" data-testid="broker-tagging-module">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Tags className="w-6 h-6 text-trading-blue" />
            Broker Tagging
          </h1>
          <p className="text-sm text-trading-muted mt-1">Classify brokers to enable institutional flow detection</p>
        </div>
        <div className="text-right">
          <span className="font-mono text-lg text-trading-text">{allBrokers.length}</span>
          <span className="text-xs text-trading-muted ml-1">active brokers</span>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-trading-muted" />
        <Input
          data-testid="broker-search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search brokers..."
          className="pl-9 bg-trading-bg border-trading-border text-trading-text h-9"
        />
      </div>

      {/* Active brokers from data */}
      {allBrokers.length > 0 && (
        <div className="bg-trading-surface border border-trading-border rounded-sm overflow-hidden mb-6">
          <div className="px-4 py-2 border-b border-trading-border bg-trading-elevated">
            <span className="text-xs uppercase tracking-widest text-trading-muted font-semibold">
              Brokers in Data ({filtered.length})
            </span>
          </div>
          <div className="divide-y divide-trading-border/50">
            {filtered.map(broker => (
              <BrokerRow
                key={broker}
                broker={broker}
                tag={brokerTags[broker] || 'OTHER'}
                onTagChange={(tag) => handleTagChange(broker, tag)}
                getTagColor={getTagColor}
              />
            ))}
          </div>
        </div>
      )}

      {/* Preset brokers */}
      {presetBrokers.length > 0 && (
        <div className="bg-trading-surface border border-trading-border rounded-sm overflow-hidden">
          <div className="px-4 py-2 border-b border-trading-border bg-trading-elevated">
            <span className="text-xs uppercase tracking-widest text-trading-muted font-semibold">
              Preset Brokers ({presetBrokers.length})
            </span>
          </div>
          <div className="divide-y divide-trading-border/50">
            {presetBrokers.map(([broker, tag]) => (
              <BrokerRow
                key={broker}
                broker={broker}
                tag={tag}
                onTagChange={(t) => handleTagChange(broker, t)}
                getTagColor={getTagColor}
              />
            ))}
          </div>
        </div>
      )}

      {allBrokers.length === 0 && presetBrokers.length > 0 && (
        <p className="text-sm text-trading-muted mt-4">
          Upload broker activity data to see active brokers here.
        </p>
      )}
    </div>
  );
}

function BrokerRow({ broker, tag, onTagChange, getTagColor }) {
  return (
    <div className="flex items-center justify-between px-4 py-2 hover:bg-trading-elevated/30 transition-colors" data-testid={`broker-row-${broker}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <span className="text-sm text-trading-text truncate">{broker}</span>
        <span className={`text-[10px] font-mono font-medium ${getTagColor(tag)}`}>{tag}</span>
      </div>
      <Select value={tag} onValueChange={onTagChange}>
        <SelectTrigger data-testid={`broker-tag-select-${broker}`} className="w-40 h-7 bg-trading-bg border-trading-border text-xs relative z-10">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-trading-surface border-trading-border z-[100]" position="popper" sideOffset={4}>
          {BROKER_TYPES.map(t => (
            <SelectItem key={t} value={t} className="text-xs text-trading-text hover:bg-trading-elevated cursor-pointer">
              {t}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
